import './ListDetailsHeader.scss'

import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CoreConstants } from 'hb4e-ui-core'
import { Icon, Button, Modal, Tooltip } from 'hb4e-ui-core-ui'
import { CoursepackForm } from 'hb4e-ui-coursepacks'
import { HasPermission } from 'hb4e-ui-authentication'
import { useAnalytics } from 'hb4e-ui-analytics'
import { useFeatureFlags } from 'hb4e-ui-env-config'
import { ProductService, ProductConstants } from 'hb4e-ui-product'

import {
  addListItemsToCart,
  resetListReordering,
  toggleIsReordering,
  updateListItemOrder,
} from '../../../../../actions'
import ListActionsDropdown from '../../../ListActionsDropdown/ListActionsDropdown'
import { listDetailPageType } from '../../../../../constants'
import { selectActiveList, selectIsReordering } from '../../../../../reducers'

const { PERM_CREATE_COURSE, PERM_ADD_TO_PERSONAL_CART, PERM_REORDER_LIST_ITEM } = CoreConstants
const { LIST_DISABLE_ADD_TO_CP_MESSAGE } = ProductConstants
const { isFutureProduct, shouldHideAddToCoursepack } = ProductService

const ListDetailsHeader = ({ list }) => {
  const { trackPageLoad } = useAnalytics()

  const dispatch = useDispatch()
  const { list: detailedList } = useSelector(selectActiveList)
  const isReordering = useSelector(selectIsReordering)

  const [isSubmittingAddToCart, setIsSubmittingAddToCart] = useState(false)
  const [isSubmittingReorder, setIsSubmittingReorder] = useState(false)
  const [isCreateAsACoursepackModalOpen, setIsCreateAsACoursepackModalOpen] = useState(false)
  const [isCreateAsCoursepackDisabled, setIsCreateAsCoursepackDisabled] = useState(false)

  // TODO: KT (04-04-2023) - Remove FF during heet-39-my-list-reordering clean up
  const { heet39MyListReordering } = useFeatureFlags()

  const handleCreateAsACoursepackButtonClick = () => {
    setIsCreateAsACoursepackModalOpen(true)
  }

  const handleAddItemsToCartButtonClick = async () => {
    setIsSubmittingAddToCart(true)
    await dispatch(addListItemsToCart(list))
    setIsSubmittingAddToCart(false)
  }

  const handleReorderClick = () => {
    dispatch(toggleIsReordering())
  }

  const handleReorderCancelClick = () => {
    dispatch(resetListReordering())
  }

  const handleReorderDoneClick = () => {
    setIsSubmittingReorder(true)
    dispatch(updateListItemOrder(detailedList))
  }

  const handleModalOnClose = () => {
    setIsCreateAsACoursepackModalOpen(false)
  }

  // Checks if List has any future product
  useEffect(() => {
    let listToCheck = detailedList ?? list
    if (listToCheck?.isPersonalList() || listToCheck?.isSavedItemsList()) {
      const products = listToCheck.getItems()

      if (products.length > 0) {
        if (
          products.some((product) => isFutureProduct(product)) ||
          products.some((product) => shouldHideAddToCoursepack(product))
        ) {
          return setIsCreateAsCoursepackDisabled(true)
        } else {
          return setIsCreateAsCoursepackDisabled(false)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!!list?.id) {
      /**
       * TODO (KR: 10/04/2022 - may need to move this from here based on discussion/design with 33 sticks
       *
       * Additional note (KM:05/13/2024) - This will need to be moved, it's producing duplicate page tracking warning.
       * The page is being tracked multiple times within this component. Could cause issues with analytics tracking.
       * */
      trackPageLoad({
        pageType: listDetailPageType,
        pageName: 'List Details Page',
      })
    }
  }, [])

  const renderAddItemsToCartButton = () => (
    <Button
      icon={isSubmittingAddToCart ? <Icon.Rotate spin /> : null}
      isDisabled={isSubmittingAddToCart}
      onClick={handleAddItemsToCartButtonClick}
      theme="primary outline"
      type="button"
    >
      Add items to Cart
    </Button>
  )

  const renderAddItemsToCartCTA = () => {
    return <HasPermission permission={[PERM_ADD_TO_PERSONAL_CART]}>{renderAddItemsToCartButton()}</HasPermission>
  }

  const getTooltipText = () => {
    return LIST_DISABLE_ADD_TO_CP_MESSAGE
  }

  const renderCreateAsCoursepackButton = () => {
    return (
      <Button
        data-testid="list-details-header-create-as-coursepack-button"
        isDisabled={isCreateAsCoursepackDisabled}
        onClick={handleCreateAsACoursepackButtonClick}
        theme="primary solid"
        type="button"
      >
        Create as a Coursepack
        {isCreateAsCoursepackDisabled && (
          <p className="list-details-header__tooltip-text-for-screen-readers">{getTooltipText()}</p>
        )}
      </Button>
    )
  }

  const renderCreateAsCoursepackTooltip = () => (
    <div className="list-details-header__tooltip-container">
      <p className="list-details-header__tooltip-text">{getTooltipText()}</p>
    </div>
  )

  const renderCreateAsCoursepackCTA = () => {
    if (isCreateAsCoursepackDisabled) {
      return (
        <HasPermission permission={[PERM_CREATE_COURSE]}>
          <div data-testid="list-details-header__tooltip-container">
            <Tooltip
              content={renderCreateAsCoursepackTooltip()}
              icon={<Icon.CircleExclamation />}
              placement={Tooltip.Placement.RIGHT}
            >
              {/* div has to be focusable so tooltip can appear for keyboard and screen reader users. */}
              <div
                className="list-details-header__create-as-coursepack"
                role="button"
                tabIndex="0"
                aria-disabled="true"
              >
                {renderCreateAsCoursepackButton()}
              </div>
            </Tooltip>
          </div>
        </HasPermission>
      )
    }

    return <HasPermission permission={[PERM_CREATE_COURSE]}>{renderCreateAsCoursepackButton()}</HasPermission>
  }

  const renderReorderCTA = () => {
    // TODO: KT (04-04-2023) - Remove FF during heet-39-my-list-reordering clean up
    if (!heet39MyListReordering) {
      return null
    }

    return (
      <HasPermission permission={[PERM_REORDER_LIST_ITEM]}>
        <Button
          icon={<Icon.ArrowUpArrowDown />}
          onClick={handleReorderClick}
          theme="primary outline icon-left"
          type="button"
        >
          Reorder
        </Button>
      </HasPermission>
    )
  }

  const renderCTASection = () => {
    if (list.isEmpty() || !list.isPersonalList()) {
      return null
    }

    if (isReordering) {
      return (
        <div className="list-details-header__buttons-wrapper is-reordering">
          <p className="list-details-header__reorder-instructions-text">Click and drag to reorder items</p>
          <Button onClick={handleReorderCancelClick} theme="primary outline" type="button">
            Cancel
          </Button>
          <Button
            icon={isSubmittingReorder ? <Icon.Rotate spin /> : null}
            isDisabled={isSubmittingReorder}
            onClick={handleReorderDoneClick}
            theme="primary solid"
            type="button"
          >
            Done
          </Button>
        </div>
      )
    }

    return (
      <div className="list-details-header__buttons-wrapper">
        <span className="list-details-header__left-buttons-wrapper">
          {renderCreateAsCoursepackCTA()}
          {renderAddItemsToCartCTA()}
        </span>
        {renderReorderCTA()}
      </div>
    )
  }

  return (
    <div className="list-details-header">
      <div className="list-details-header__title-and-options-wrapper">
        <h2 className="list-details-header__list-name">{list.getName()}</h2>
        {list.isPersonalList() && (
          <div className="list-details-header__actions-dropdown">
            <ListActionsDropdown list={list} />
          </div>
        )}
      </div>

      <p className="list-details-header__list-description">{list.getDescription()}</p>

      {list.createdDate && list.isPersonalList() && (
        <p className="list-details-header__list-created-date">
          <span className="list-details-header__list-created-date-label">Created: </span>
          <span className="list-details-header__list-created-date-value">{list.getCreatedDateFormatted()}</span>
        </p>
      )}

      {list.updatedDate && (
        <p className="list-details-header__list-last-updated-date">{`Last updated on ${list.getUpdatedDateFormatted()}`}</p>
      )}

      {renderCTASection()}

      <Modal
        title={'Create as a Coursepack'}
        isOpen={isCreateAsACoursepackModalOpen}
        onClose={handleModalOnClose}
        className="list-details-header__actions-modal"
      >
        <CoursepackForm
          onClose={handleModalOnClose}
          onAfterSubmit={() => {}}
          type={CoursepackForm.Type.CREATE}
          listID={list.getServerID()}
          productToAddFromList={list.getItems()}
        />
      </Modal>
    </div>
  )
}

export default ListDetailsHeader
